import { useAuth } from "@hooks/useAuth";
import { useDataMutation } from "@hooks/useData/useDataMutation";
import { useDataQuery } from "@hooks/useData/useDataQuery";
import { useUserSubscriptions } from "@hooks/useUserSubscriptions";
import { env } from "@src/env.mjs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { serverEndpoints } from "@utils/serverEndpoints";
import { authClient } from "@utils/authClient";
import { selectData, supabase } from "@utils/serverClient";
import { SignagePlan } from "./types";

type PlansResponse = {
  success: boolean;
  data: SignagePlan[];
};

export const usePlans = () => {
  const { user, organization } = useAuth();
  const { planProperties } = useUserSubscriptions();

  const queryKey = ["plans"];
  const queryClient = useQueryClient();

  const {
    data: plansResponse,
    status: plansFetchStatus,
    refetch: getPlans,
  } = useQuery<PlansResponse>({
    queryKey,
    enabled: user?.id != undefined,
    gcTime: 1 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (!user?.id) return;

      const accessToken = await authClient.getAccessToken();

      if (!accessToken) return [];
      const data = await fetch(serverEndpoints.getPlans, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const _json = await data.json();

      return _json;
    },
  });

  const { mutate: updatePlan } = useDataMutation({
    mutationFn: async ({ plan }: { plan: Omit<Partial<SignagePlan>, 'id'> & { id: SignagePlan['id'] } }) => {
      if (!user?.id) return;
      if (!organization?.id) return;

      const accessToken = await authClient.getAccessToken();

      const data = await fetch(serverEndpoints.updatePlan, {
        method: "POST",
        body: JSON.stringify({ plan }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          "user-id": user?.id ?? "",
          "business-id": organization?.id ?? "",
        },
      });

      const _json = await data.json();

      return _json;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to update plan",
    optimisticUpdateFn: (oldData: PlansResponse | undefined, { plan }) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        data: oldData.data.map(p => p.id === plan.id ? { ...p, ...plan } : p)
      }
    },
  });

  const { mutate: addPlan } = useMutation({
    mutationFn: async ({ plan }: { plan: Omit<SignagePlan, "id"> }) => {
      if (!user?.id) return;
      if (!organization?.id) return;

      const accessToken = await authClient.getAccessToken();

      const data = await fetch(serverEndpoints.createPlan, {
        method: "POST",
        body: JSON.stringify({
          plan: plan,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          "user-id": user?.id ?? "",
          "business-id": organization?.id ?? "",
        },
      });

      const _json = await data.json();

      return _json;
    },
    onSuccess(data) {
      if (data.success && data.data) {
        queryClient.setQueryData(
          queryKey,
          (old: PlansResponse | undefined) => {
            if (old) {
              const _newdata = [...old.data, data.data];

              return {
                success: old.success,
                data: _newdata,
              };
            }
            return old;
          },
        );
      }
    },
  });

  const { mutate: deletePlan } = useDataMutation({
    mutationFn: async ({ planId }: { planId: SignagePlan['id'] }) => {
      if (!user?.id) return;
      if (!organization?.id) return;

      const accessToken = await authClient.getAccessToken();

      const data = await fetch(serverEndpoints.deletePlan, {
        method: "DELETE",
        body: JSON.stringify({
          planId: planId,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          "user-id": user?.id ?? "",
          "business-id": organization?.id ?? "",
        },
      });

      const _json = await data.json();

      return _json;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to delete plan",
    optimisticUpdateFn: (oldData: PlansResponse | undefined, { planId }) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        data: oldData.data.filter(p => p.id !== planId)
      }
    }
  });

  const getMusicStatus = () => {
    if (!organization) throw new Error("Organization not found");
    if (env.NEXT_PUBLIC_IS_DEV) return "demo";
    return organization.plan_status === 'free_trial' ? 'demo' : currentMusicZoneCount === 0 ? 'demo' : 'active';
  };

  const getMusicZoneLimit = () => {
    if (organization?.plan_status === "free_trial") return 1;
    return planProperties?.entitled_music_zones || 1;
  };

  const { data: currentMusicZoneCount = 0 } = useDataQuery({
    queryKey: ["music-zone-count", organization?.id],
    async queryFn() {
      if (!organization?.id) return 0
      const { data: screens, error } = await selectData({
        table: 'screen',
        columns: 'id',
      })
        .eq('org_id', organization.id)
        .eq('music_integration_enabled', true);

      if (error) {
        console.error(`Error getting demo count for org ${organization.id}:`, error);
        throw error;
      }

      return screens?.length ?? 0;
    }
  })

  const getCurrentMusicZoneCount = () => {
    return currentMusicZoneCount;
  };

  const getMusicCost = () => {
    if (currentMusicZoneCount === 0)
      return planProperties?.music_integration_cost || 0;

    return planProperties?.addon_music_cost || 0;
  };

  return {
    plansFetchStatus,
    plans: plansResponse?.data ?? [],
    getPlans,
    updatePlan,
    addPlan,
    deletePlan,
    getMusicStatus,
    getMusicZoneLimit,
    getCurrentMusicZoneCount,
    getMusicCost,
  }
};
